
import { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Download, Printer, Check, ArrowLeft, Loader2, FileText } from 'lucide-react';
import { GeneratedInvoice } from '../../types/taxInvoice';

interface TaxInvoicePreviewProps {
  invoice: GeneratedInvoice;
  onConfirm: () => void;
  onBack: () => void;
  isProcessing: boolean;
}

export const TaxInvoicePreview = ({
  invoice,
  onConfirm,
  onBack,
  isProcessing
}: TaxInvoicePreviewProps) => {
  const { language } = useLanguage();
  const isRtl = language === 'ar';
  const [isPdfLoading, setIsPdfLoading] = useState(true);

  const handleDownload = () => {
    // Create blob from base64
    const byteCharacters = atob(invoice.pdfBase64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${invoice.invoiceNumber}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    // Create blob and open in new window for printing
    const byteCharacters = atob(invoice.pdfBase64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    
    const url = window.URL.createObjectURL(blob);
    const printWindow = window.open(url, '_blank');
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  const pdfDataUrl = `data:application/pdf;base64,${invoice.pdfBase64}`;

  return (
    <div className={`space-y-6 ${isRtl ? 'rtl' : 'ltr'}`} dir={isRtl ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <FileText className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">
              {isRtl ? 'معاينة الفاتورة' : 'Invoice Preview'}
            </h2>
            <p className="text-sm text-muted-foreground">
              {isRtl ? `رقم الفاتورة: ${invoice.invoiceNumber}` : `Invoice #: ${invoice.invoiceNumber}`}
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleDownload}>
            <Download className="h-4 w-4 mr-2" />
            {isRtl ? 'تحميل' : 'Download'}
          </Button>
          <Button variant="outline" size="sm" onClick={handlePrint}>
            <Printer className="h-4 w-4 mr-2" />
            {isRtl ? 'طباعة' : 'Print'}
          </Button>
        </div>
      </div>

      {/* PDF Preview */}
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <div className="relative bg-muted min-h-[500px]">
            {isPdfLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-muted">
                <div className="text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                  <p className="mt-2 text-sm text-muted-foreground">
                    {isRtl ? 'جاري تحميل الفاتورة...' : 'Loading invoice...'}
                  </p>
                </div>
              </div>
            )}
            <iframe
              src={pdfDataUrl}
              className="w-full h-[600px] border-0"
              title="Invoice Preview"
              onLoad={() => setIsPdfLoading(false)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between items-center pt-4 border-t">
        <Button variant="ghost" onClick={onBack} disabled={isProcessing}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {isRtl ? 'رجوع' : 'Back'}
        </Button>

        <Button onClick={onConfirm} disabled={isProcessing} className="min-w-[180px]">
          {isProcessing ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              {isRtl ? 'جاري المعالجة...' : 'Processing...'}
            </>
          ) : (
            <>
              <Check className="h-4 w-4 mr-2" />
              {isRtl ? 'تأكيد والمتابعة' : 'Confirm & Proceed'}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
